import { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  useSendEcardGuestEmailMutation,
  useSaveEcardMutation,
  useSaveEcardEmailMutation,
  EcardType,
  useSaveEcardDesignMutation,
  useGetEcardDataQuery,
  EventDesignFragment,
} from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import {
  getEcardInputState,
  getEcardPayload,
  getInitialEcardState,
  getEcardEmailPayload,
  getEmailInputState,
  isValidDate,
  getEventWebsiteUrl,
  getEcardDesignPayload,
  isCustomECardType,
  getFilteredLinkOptions,
  ecardStateDefault,
  getDateOnly,
} from '@apps/ecard/Ecard.utils';
import { useImmer } from 'use-immer';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { EcardState } from '@apps/ecard/Ecard.types';
import { useEcardTranslations } from '@apps/ecard/Ecard.i18n';
import { PeopleContext } from '@shared/context/people';
import { useEcardsTelemetry } from '@apps/ecard/Ecard.telemetry';
import { getFooterTextWithCouplesNames } from '@shared/components/EmailsAndEcards/Emails.utils';
import { getReferrer } from '@apps/admin/postOffice/PostOffice';
import { useQueryParamHelper } from '@shared/core/queryString';
import { usePostOfficeBottomSheetState } from '@apps/admin/postOffice/PostOffice.bottomSheet';
import { useCopyLinkHandlers } from '@shared/hooks/useCopyLinkHandlers';
import { useTranslation } from '@shared/core';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { useShareableCardLinkGenerator } from '@shared/hooks/useShareableLinkCardGenerator';
import { useUnpublishedWebsiteWarning } from '@shared/components/UnpublishedWebsiteWarning/useUnpublishedWebsiteWarning';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { DateOnlyFormat } from '@localization/formatters/dateFormatter';

interface UseEcardControllerArgs
  extends Readonly<{
    eventId: string;
    eventDisplayName: string;
    ecardDraftType: EcardType;
    eventHandle: string;
  }> {}

interface HandleSendEmailArgs {
  testEmail?: string;
  testName?: string;
  onCompleted?: () => void;
  onError?: () => void;
}

export const useEcardController = (args: UseEcardControllerArgs) => {
  const { eventId, eventDisplayName, ecardDraftType, eventHandle } = args;
  const { getValueString } = useQueryParamHelper();
  const referrer = getValueString('referrer');
  const { setParentViewState } = usePostOfficeBottomSheetState();
  const { t, tq } = useTranslation('emailsAndEcards');

  const { selectedPeople } = useContext(PeopleContext);
  const {
    currentUser: { profile: userProfile },
  } = useAuth();

  const telemetry = useEcardsTelemetry();

  const { data: ecardData, loading: loadingDraftData } = useGetEcardDataQuery({
    batchMode: 'off',
    fetchPolicy: 'no-cache',
    variables: { eventId, ecardType: ecardDraftType },
    onError(error) {
      telemetry.trackError('Ecard.controller - GetEcardData - error loading data', error, ecardDraftType);
    },
  });
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: ecardData?.eventById?.pages, layoutType: ecardData?.eventById?.eventDesign?.websiteLayout.layoutType });

  const [sendEcardGuestEmail] = useSendEcardGuestEmailMutation();
  const [saveEcard] = useSaveEcardMutation();
  const [saveEcardEmail] = useSaveEcardEmailMutation();
  const [saveEcardDesign] = useSaveEcardDesignMutation();

  const { eventPassword, eventPhotoUrl, isPasswordToggleShown, pages, eventDesign, eventInfo, privacyMode, domainName, website, linkOptions, footerText, eventDate, location } =
    useMemo(() => {
      const eventById = ecardData?.eventById;
      const eventInfo = eventById?.info;
      const privacyMode = eventById?.settings?.privacyMode;
      const ownerFirstName = eventInfo?.ownerFirstName;
      const fianceeFirstName = eventInfo?.fianceeFirstName;
      const filteredLinkOptions = ecardData?.getEcardLinkOptionsByEventId ? getFilteredLinkOptions(ecardData?.getEcardLinkOptionsByEventId, ecardDraftType) : [];

      return {
        eventPassword: eventById?.info.eventPassword,
        eventPhotoUrl: eventById?.photo?.url,
        pages: guestSitePages,
        eventDesign: eventById?.eventDesign,
        eventInfo,
        privacyMode,
        isPasswordToggleShown: !!(privacyMode && privacyMode !== 'public'),
        domainName: eventById?.domain?.domainName,
        website: eventById?.website,
        linkOptions: filteredLinkOptions,
        footerText: getFooterTextWithCouplesNames(ownerFirstName, fianceeFirstName, userProfile?.email, t),
        eventDate: eventInfo?.finalizedDate?.timestamp,
        location: eventInfo?.location,
      };
    }, [ecardData?.eventById, ecardData?.getEcardLinkOptionsByEventId, ecardDraftType, guestSitePages, t, userProfile?.email]);

  const { getEcardDefaultTranslations, ecardNavBarTranslations } = useEcardTranslations({ ecardDraftType });

  const [state, setState] = useImmer<EcardState>(ecardStateDefault);

  const { formatDate } = useEventLocaleFormatter();

  const isCustomECard = useMemo(() => isCustomECardType(ecardDraftType), [ecardDraftType]);
  const formattedECardDate = isCustomECard ? state.ecardInput.date : formatDate(getDateOnly(state.ecardInput.date), state.ecardInput.dateFormat as DateOnlyFormat);

  const ecardTheme = useMemo(() => state.ecardInput.joyTheme?.themeId || '', [state]);

  let adminReferrer = getReferrer();
  if (referrer) {
    adminReferrer = referrer;
  }

  useEffect(() => {
    const newEcardState: EcardState = getInitialEcardState({
      eventId,
      userProfile,
      eventPhotoUrl,
      tEcardDefaults: getEcardDefaultTranslations(eventDisplayName),
      ecardDraftType,
      eventHandle,
      eventDate,
      linkOptions,
      eventDesign,
      location,
      adminReferrer,
    });

    let newEcardInput: EcardState['ecardInput'] = { ...newEcardState.ecardInput };
    let newEmailInput: EcardState['emailInput'] = { ...newEcardState.emailInput };

    if (!ecardData) {
      setState(prev => ({
        ...prev,
        ecardInput: newEcardInput,
        emailInput: newEmailInput,
        adminReferrer: newEcardState.adminReferrer,
      }));

      return;
    }

    if (!ecardData?.getMostRecentEcardByEventId) {
      newEcardInput = {
        ...newEcardState.ecardInput,
      };
    } else {
      const savedEcard = ecardData?.getMostRecentEcardByEventId;
      newEcardInput = getEcardInputState(newEcardInput, savedEcard, linkOptions ?? [], location, eventDate);
    }

    if (ecardData?.getEcardEmailDraftsByEventId.length === 0) {
      newEmailInput = {
        ...newEcardState.emailInput,
      };
      if (!newEmailInput.imageUrl && eventPhotoUrl) {
        newEmailInput = {
          ...newEmailInput,
          imageUrl: eventPhotoUrl,
        };
      }
    } else {
      const savedEmail = ecardData?.getEcardEmailDraftsByEventId[0];
      newEmailInput = getEmailInputState(newEmailInput, savedEmail, newEcardInput.date, pages, eventPhotoUrl);
    }

    if (domainName || website) {
      newEcardInput = {
        ...newEcardInput,
        yourUrl: getEventWebsiteUrl(website, domainName),
      };
    }

    setState(prev => ({
      ...prev,
      ecardInput: newEcardInput,
      emailInput: newEmailInput,
      adminReferrer: newEcardState.adminReferrer,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecardData]);

  const onSaveEcardDesign = useCallback(
    async (eventDesign: Maybe<EventDesignFragment>, ecardId: string, ecardInput: EcardState['ecardInput']) => {
      if (ecardInput.joyTheme && ecardInput.joyTheme.themeId && eventDesign) {
        const ecardDesignPayload = getEcardDesignPayload(eventDesign, ecardId, ecardInput);
        try {
          await saveEcardDesign({
            variables: {
              payload: ecardDesignPayload,
            },
          });
        } catch (error) {
          telemetry.trackError('SaveEcardDesignMutation', error, ecardDraftType);
          throw error;
        }
      }
    },
    [ecardDraftType, saveEcardDesign, telemetry],
  );

  const updateEmailInput = useEventCallback(async (emailInput: EcardState['emailInput']) => {
    setState(draft => {
      draft.emailInput = emailInput;
    });
    let ecardId = state.ecardInput.id;
    if (!ecardId) {
      // no ecardId - couple never edited their ecard and never saved it but they are saving ecard email - save ecard data and design
      const eCardPayload = getEcardPayload(state.ecardInput);
      telemetry.eCardSave(ecardDraftType, eCardPayload, ecardTheme);
      try {
        const res = await saveEcard({
          variables: {
            payload: eCardPayload,
          },
        });
        ecardId = res.data?.saveEcard?.id;
        if (ecardId) {
          await onSaveEcardDesign(eventDesign, ecardId, state.ecardInput);
        }
      } catch (error) {
        telemetry.trackError('SaveEcardMutation', error as Error, ecardDraftType);
        console.error(error);
      }
    }
    if (ecardId) {
      const emailPayload = {
        ...getEcardEmailPayload(emailInput, ecardId, ecardDraftType, state.ecardInput.date),
        ecardType: ecardDraftType as EcardType,
      };
      telemetry.emailSave(emailPayload);
      try {
        saveEcardEmail({
          variables: {
            payload: emailPayload,
          },
        });
      } catch (error) {
        telemetry.trackError('SaveEcardEmailMutation', error, ecardDraftType);
      }
    }
  });

  const updateEcardInput = useEventCallback(async (ecardInput: EcardState['ecardInput']) => {
    const eCardPayload = getEcardPayload(ecardInput);
    telemetry.eCardSave(ecardDraftType, eCardPayload, ecardTheme);
    try {
      const res = await saveEcard({
        variables: {
          payload: eCardPayload,
        },
      });
      const ecardId = res.data?.saveEcard?.id;
      if (ecardId) {
        setState(draft => {
          if (draft.ecardInput.date !== ecardInput.date) {
            draft.emailInput.includeAddToCalendarLink = isValidDate(ecardInput.date) ? draft.emailInput.includeAddToCalendarLink : false;
          }
          draft.ecardInput = { ...ecardInput, id: ecardId };
        });
        await onSaveEcardDesign(eventDesign, ecardId, ecardInput);
      }
    } catch (error) {
      telemetry.trackError('SaveEcardMutation', error, ecardDraftType);
      throw error;
    }
  });

  const { openUnpublishedWebsiteWarning, warningProps } = useUnpublishedWebsiteWarning({
    context: { category: 'admin.messaging', page: 'confirmAndSendTrack' },
  });

  const handleSendEmail = useCallback(
    async (args: HandleSendEmailArgs) => {
      await openUnpublishedWebsiteWarning();
      telemetry.sendECardClick(ecardDraftType, { eCardData: state.ecardInput, emailData: state.emailInput }, selectedPeople.length.toString(), state.adminReferrer);
      let ecardId = state.ecardInput.id;
      if (!ecardId) {
        // no ecardId - couple never edited their ecard and never saved it but they are sending ecard email - save ecard data and design
        const eCardPayload = getEcardPayload(state.ecardInput);
        telemetry.eCardSave(ecardDraftType, eCardPayload, ecardTheme);
        try {
          const res = await saveEcard({
            variables: {
              payload: eCardPayload,
            },
          });
          ecardId = res.data?.saveEcard?.id;
          if (ecardId) {
            setState(draft => {
              draft.ecardInput.id = ecardId;
            });
            onSaveEcardDesign(eventDesign, ecardId, state.ecardInput);
          }
        } catch (error) {
          telemetry.trackError('SaveEcardMutation', error, ecardDraftType);
        }
      }
      const { testEmail, testName, onCompleted, onError } = args;

      const emailPayload = getEcardEmailPayload(
        state.emailInput,
        ecardId!,
        ecardDraftType,
        formattedECardDate || undefined,
        testName,
        testEmail,
        selectedPeople,
        footerText,
        eventInfo?.location || '',
      );
      if (testName && testEmail) {
        telemetry.sendTestEmail(ecardDraftType, testName, testEmail);
      } else {
        telemetry.sendEmail(ecardDraftType, emailPayload);
      }
      await sendEcardGuestEmail({
        variables: {
          payload: emailPayload,
        },
      })
        .then(() => {
          if (onCompleted) {
            onCompleted();
          }
        })
        .catch(error => {
          telemetry.trackError('SendEcardGuestEmailMutation', error, ecardDraftType);
          if (onError) {
            onError();
          }
        });
    },
    [
      ecardDraftType,
      ecardTheme,
      eventDesign,
      eventInfo?.location,
      footerText,
      formattedECardDate,
      onSaveEcardDesign,
      openUnpublishedWebsiteWarning,
      saveEcard,
      selectedPeople,
      sendEcardGuestEmail,
      setState,
      state.adminReferrer,
      state.ecardInput,
      state.emailInput,
      telemetry,
    ],
  );

  const shareableLinkOptions = useMemo(
    () => ({
      eventId,
      eventPassword: eventPassword || null,
      saveEcardMutation: saveEcard,
      onSaveEcardDesign,
      onSuccess: () => {
        // Handle success - call telemetry here
        const eCardPayload = getEcardPayload(state.ecardInput);
        telemetry.eCardSave(ecardDraftType, eCardPayload, ecardTheme);
      },
      onError: (error: Error, context?: EcardType) => {
        // Handle error - call telemetry here
        telemetry.trackError('Shareable link - creation', error, context || ecardDraftType);
      },
      eventDesign,
      ecardInput: state.ecardInput,
      ecardType: ecardDraftType,
    }),
    [ecardDraftType, eventPassword, eventId, eventDesign, state.ecardInput, saveEcard, telemetry, ecardTheme, onSaveEcardDesign],
  );

  const { generateShareableLink } = useShareableCardLinkGenerator(shareableLinkOptions);

  const copyLinkOptions = useMemo(
    () => ({
      successMessage: tq`✨ Link copied. Share away! ✨`,
      onSuccess: (generatedLink: string) => {
        telemetry.copyShareableLinkClicked(ecardDraftType, generatedLink);
      },
      onError: (error: Error, context?: EcardType) => {
        telemetry.trackError('Shareable link - copy', error, context || ecardDraftType);
      },
      context: ecardDraftType,
    }),
    [ecardDraftType, tq, telemetry],
  );
  const { handleCopyShareableLink: copyShareableLink } = useCopyLinkHandlers<EcardType>(copyLinkOptions);

  const handleCopyShareableLink = useCallback(
    async (link: string) => {
      copyShareableLink(link);
      await openUnpublishedWebsiteWarning();
    },
    [copyShareableLink, openUnpublishedWebsiteWarning],
  );

  useEffect(() => {
    setParentViewState(prev => ({ ...prev, handleSendEmail, eventHandle, handleCopyShareableLink, generateShareableLink }));
  }, [handleSendEmail, setParentViewState, eventHandle, handleCopyShareableLink, generateShareableLink]);

  return {
    aboutShareableLinksClick: telemetry.aboutShareableLinksClick,
    handleSendEmail,
    loadingDraftData,
    eventPassword,
    eventPhotoUrl,
    isPasswordToggleShown,
    pages,
    eventDesign,
    eventInfo,
    ecardState: state,
    updateEmailInput,
    updateEcardInput,
    ecardNavBarTranslations,
    testEmail: userProfile?.email ?? undefined,
    privacyMode,
    eventDate: formattedECardDate,
    footerText,
    linkOptions,
    emailIsVerified: !!userProfile?.emailIsVerified,
    handleCopyShareableLink,
    generateShareableLink,
    unpublishedWarningProps: warningProps,
  };
};
